import React, { useMemo, useState } from 'react';
import { CellInfo } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from 'src/containers/NavBar';
import Flex from 'src/components/Flex';
import Button from 'src/components/Button';
import Table from 'src/components/Table';
import Card from 'src/components/Card';
import Icon from 'src/components/Icon';
import Input from 'src/components/Input';
import SideModal from 'src/components/SideModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import { ICONS, ROUTES } from 'src/utils/constants';
import { parseError } from 'src/utils';
import { ToastStoreObject } from 'src/stores/ToastStore';
import {
    CreateDocumentScanTypeInput,
    DocumentScanCaptureMode,
    DocumentScanSupportedEntityType,
    DocumentScanTypeAdminSummary,
    DocumentScanTypeTemplate,
    DocumentScanTypeVersionSummary,
    useAdminDocumentScanTypes,
    useCreateDocumentScanType,
    useCreateDocumentScanTypeFromTemplate,
    useDocumentScanTypeTemplates,
} from 'src/queries/useDocumentScanTypesAdmin';
import { buildEncounterEntityMappingSchemaSubset } from 'common/lib/model/documentScan/encounterEntityMappingSchema';
import DocumentTypeDetail from './Detail';
import styles from './styles.module.scss';

type CreateMode = 'template' | 'scratch';

interface CreateFormState {
    mode: CreateMode;
    templateKey: string;
    scanTypeKey: string;
    name: string;
    description: string;
    supportedEntityType: DocumentScanSupportedEntityType;
    captureMode: DocumentScanCaptureMode;
}

const defaultForm: CreateFormState = {
    mode: 'scratch',
    templateKey: '',
    scanTypeKey: '',
    name: '',
    description: '',
    supportedEntityType: 'encounter',
    captureMode: 'unnamed_pages',
};

function formatValue(value: string | null | undefined) {
    if (!value) {
        return 'None';
    }
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatCaptureMode(captureMode: DocumentScanCaptureMode | null | undefined) {
    if (captureMode === 'unnamed_pages') {
        return 'Document Pages';
    }
    if (captureMode === 'named_slots') {
        return 'Labeled Photos';
    }
    return formatValue(captureMode);
}

function getDisplayVersion(documentScanType: DocumentScanTypeAdminSummary): DocumentScanTypeVersionSummary | null {
    return documentScanType.draftVersion || documentScanType.publishedVersion;
}

function getVersionStatus(documentScanType: DocumentScanTypeAdminSummary) {
    if (documentScanType.draftVersion && documentScanType.publishedVersion) {
        return 'Draft changes';
    }
    if (documentScanType.draftVersion) {
        return 'Draft';
    }
    if (documentScanType.publishedVersion) {
        return 'Published';
    }
    return 'Unconfigured';
}

function getDefaultCaptureConfig(captureMode: DocumentScanCaptureMode): Record<string, unknown> {
    if (captureMode === 'named_slots') {
        return {
            slots: [{ slotKey: 'front', label: 'Front', required: true, maxImages: 1 }],
        };
    }

    return { minImages: 1, maxImages: 10 };
}

function buildScratchPayload(form: CreateFormState): CreateDocumentScanTypeInput {
    return {
        scanTypeKey: form.scanTypeKey.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        supportedEntityType: form.supportedEntityType,
        promptText: 'Extract structured data from the supplied document images.',
        extractionSchema: { type: 'object', additionalProperties: true, properties: {} },
        entityMappingSchema:
            form.supportedEntityType === 'encounter' ? buildEncounterEntityMappingSchemaSubset([]) : null,
        captureMode: form.captureMode,
        captureConfig: getDefaultCaptureConfig(form.captureMode),
        allowEntityCreation: false,
        entityCreationConfig: null,
        updateLinkedEntity: false,
        requiresReviewBeforeEntityUpdate: false,
    };
}

function buildTemplateForm(template: DocumentScanTypeTemplate): CreateFormState {
    return {
        ...defaultForm,
        mode: 'template',
        templateKey: template.templateKey,
        scanTypeKey: template.scanTypeKey,
        name: template.displayName,
        description: template.description || '',
        supportedEntityType: template.supportedEntityType,
        captureMode: template.captureMode,
    };
}

function CreateDocumentTypeModal({
    form,
    templates,
    isCreating,
    onChange,
    onClose,
    onSubmit,
}: {
    form: CreateFormState;
    templates: DocumentScanTypeTemplate[];
    isCreating: boolean;
    onChange: (updates: Partial<CreateFormState>) => void;
    onClose: () => void;
    onSubmit: () => void;
}) {
    const isTemplateMode = form.mode === 'template';
    const selectedTemplate = templates.find((template) => template.templateKey === form.templateKey);
    const setMode = (mode: CreateMode) => {
        if (mode === 'template') {
            const firstTemplate = selectedTemplate || templates[0];
            onChange(firstTemplate ? buildTemplateForm(firstTemplate) : { mode: 'template' });
            return;
        }

        onChange({
            ...defaultForm,
            scanTypeKey: form.mode === 'scratch' ? form.scanTypeKey : '',
            name: form.mode === 'scratch' ? form.name : '',
            description: form.mode === 'scratch' ? form.description : '',
        });
    };

    return (
        <SideModal isOpen={true} onClose={onClose}>
            <div data-test-id="document-type-create-modal">
                <ModalHeader title="Add Document Type" onClose={onClose} />
                <Flex direction="column" className={styles.modalContent}>
                    <div className={styles.createModeGrid} data-test-id="document-type-create-mode-grid">
                        <button
                            type="button"
                            className={`${styles.createModeButton} ${!isTemplateMode ? styles.createModeActive : ''}`}
                            onClick={() => setMode('scratch')}
                            data-test-id="document-type-mode-scratch"
                        >
                            Start from Scratch
                        </button>
                        <button
                            type="button"
                            className={`${styles.createModeButton} ${isTemplateMode ? styles.createModeActive : ''}`}
                            onClick={() => setMode('template')}
                            disabled={templates.length === 0}
                            data-test-id="document-type-mode-template"
                        >
                            Use a Template
                        </button>
                    </div>
                    {isTemplateMode && (
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>Template</span>
                            <select
                                value={form.templateKey}
                                onChange={(event) => {
                                    const template = templates.find((item) => item.templateKey === event.target.value);
                                    if (template) {
                                        onChange(buildTemplateForm(template));
                                    }
                                }}
                                className={styles.select}
                                data-test-id="document-type-template-select"
                            >
                                {templates.map((template) => (
                                    <option key={template.templateKey} value={template.templateKey}>
                                        {template.displayName}
                                    </option>
                                ))}
                            </select>
                            {selectedTemplate && (
                                <span className={styles.helpText}>{selectedTemplate.description}</span>
                            )}
                        </label>
                    )}
                    <Input
                        label="Key"
                        value={form.scanTypeKey}
                        onChangeText={(scanTypeKey: string) => onChange({ scanTypeKey })}
                        required={true}
                        data-test-id="document-type-key-input"
                    />
                    <Input
                        label="Display Name"
                        value={form.name}
                        onChangeText={(name: string) => onChange({ name })}
                        required={true}
                        data-test-id="document-type-name-input"
                    />
                    <Input
                        label="Description"
                        value={form.description}
                        onChangeText={(description: string) => onChange({ description })}
                        multiLine={true}
                        rows={3}
                        data-test-id="document-type-description-input"
                    />
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>Entity</span>
                        <select
                            value={form.supportedEntityType}
                            onChange={(event) =>
                                onChange({ supportedEntityType: event.target.value as DocumentScanSupportedEntityType })
                            }
                            className={styles.select}
                            disabled={isTemplateMode}
                            data-test-id="document-type-entity-select"
                        >
                            <option value="encounter">Encounter</option>
                            <option value="encounter_form">Encounter Form</option>
                        </select>
                    </label>
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>Capture Mode</span>
                        <select
                            value={form.captureMode}
                            onChange={(event) =>
                                onChange({ captureMode: event.target.value as DocumentScanCaptureMode })
                            }
                            className={styles.select}
                            disabled={isTemplateMode}
                            data-test-id="document-type-capture-mode-select"
                        >
                            <option value="unnamed_pages">{formatCaptureMode('unnamed_pages')}</option>
                            <option value="named_slots">{formatCaptureMode('named_slots')}</option>
                        </select>
                    </label>
                </Flex>
                <ModalFooter
                    primaryText="Create"
                    primaryClick={onSubmit}
                    secondaryClick={onClose}
                    isLoading={isCreating}
                    primaryProps={{
                        disabled:
                            !form.scanTypeKey.trim() || !form.name.trim() || (isTemplateMode && !form.templateKey),
                        'data-test-id': 'create-document-type-submit',
                    }}
                    secondaryProps={{ 'data-test-id': 'create-document-type-cancel' }}
                />
            </div>
        </SideModal>
    );
}

function DocumentTypesList() {
    const history = useHistory();
    const {
        data: templates = [],
        isLoading: templatesLoading,
        error: templatesError,
        refetch: refetchTemplates,
    } = useDocumentScanTypeTemplates();
    const {
        data: documentScanTypes = [],
        isLoading: scanTypesLoading,
        error: scanTypesError,
        refetch: refetchScanTypes,
    } = useAdminDocumentScanTypes();
    const { mutate: createFromTemplate, isLoading: isCreatingFromTemplate } = useCreateDocumentScanTypeFromTemplate();
    const { mutate: createScratch, isLoading: isCreatingScratch } = useCreateDocumentScanType();
    const [createForm, setCreateForm] = useState<CreateFormState | null>(null);

    const columns = useMemo(
        () => [
            {
                Header: 'DOCUMENT TYPE',
                accessor: 'documentScanTypeId',
                minWidth: 220,
                Cell: (props: CellInfo) => {
                    const scanType = documentScanTypes.find((item) => item.documentScanTypeId === props.value);
                    return (
                        <Flex direction="column">
                            <span className={styles.primaryCellText}>{scanType?.displayName || 'Untitled'}</span>
                            <span className={styles.secondaryCellText}>{scanType?.scanTypeKey}</span>
                        </Flex>
                    );
                },
            },
            {
                Header: 'STATUS',
                accessor: 'hasDraft',
                minWidth: 140,
                Cell: (props: CellInfo) => {
                    const scanType = props.original as DocumentScanTypeAdminSummary;
                    return <span className={styles.statusBadge}>{getVersionStatus(scanType)}</span>;
                },
            },
            {
                Header: 'ENTITY',
                accessor: 'supportedEntityType',
                minWidth: 140,
                Cell: (props: CellInfo) => formatValue(props.value),
            },
            {
                Header: 'CAPTURE',
                accessor: 'captureMode',
                minWidth: 160,
                Cell: (props: CellInfo) => {
                    const scanType = props.original as DocumentScanTypeAdminSummary;
                    const displayVersion = getDisplayVersion(scanType);
                    return formatCaptureMode(displayVersion?.captureMode);
                },
            },
            {
                Header: 'PUBLISHED',
                accessor: 'publishedVersion',
                minWidth: 120,
                Cell: (props: CellInfo) => (props.value ? `v${props.value.versionNumber}` : 'Not published'),
            },
            {
                Header: 'ACTIONS',
                accessor: 'documentScanTypeId',
                minWidth: 120,
                Cell: (props: CellInfo) => (
                    <Button
                        type="small"
                        text="Edit"
                        onClick={() =>
                            history.push(
                                ROUTES.getString(ROUTES.DocumentTypeDetail, undefined, {
                                    documentScanTypeId: props.value,
                                }),
                            )
                        }
                        data-test-id={`edit-document-type-${props.value}`}
                    />
                ),
            },
        ],
        [documentScanTypes, history],
    );

    const handleCreateScratch = () => {
        setCreateForm({ ...defaultForm });
    };

    const handleCreateSubmit = () => {
        if (!createForm) {
            return;
        }

        const options = {
            onSuccess: () => {
                setCreateForm(null);
                ToastStoreObject.show('Document type draft created');
            },
            onError: (error: unknown) => ToastStoreObject.show(parseError(error)),
        };

        if (createForm.mode === 'template') {
            createFromTemplate(
                {
                    templateKey: createForm.templateKey,
                    scanTypeKey: createForm.scanTypeKey.trim(),
                    name: createForm.name.trim(),
                    description: createForm.description.trim() || null,
                    supportedEntityType: createForm.supportedEntityType,
                },
                options,
            );
            return;
        }

        createScratch(buildScratchPayload(createForm), options);
    };

    const error = templatesError || scanTypesError;
    const isLoading = templatesLoading || scanTypesLoading;
    const isCreating = isCreatingFromTemplate || isCreatingScratch;

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="row" align="center" justify="between" className={styles.header}>
                        <Flex direction="column">
                            <h1 className={styles.pageTitle}>Document Types</h1>
                            <p className={styles.pageSubtitle}>
                                Manage Capture AI document definitions and draft configurations
                            </p>
                        </Flex>
                        <Button
                            type="primary"
                            text="Add Document Type"
                            leftIcon={<Icon name={ICONS.PlusButton} className={styles.plusIcon} />}
                            onClick={handleCreateScratch}
                            data-test-id="create-scratch-document-type-button"
                        />
                    </Flex>

                    {error && (
                        <Card className={styles.errorCard} data-test-id="document-types-error-card">
                            <div className={styles.errorMessage}>
                                <strong>Error loading document types:</strong> {parseError(error)}
                                <Button
                                    type="small"
                                    text="Retry"
                                    onClick={() => {
                                        refetchTemplates();
                                        refetchScanTypes();
                                    }}
                                    className={styles.retryButton}
                                    data-test-id="document-types-retry-button"
                                />
                            </div>
                        </Card>
                    )}

                    <section className={styles.section}>
                        <Flex direction="row" align="center" justify="between" className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Org Document Types</h2>
                        </Flex>
                        <Card className={styles.tableCard}>
                            <Table
                                columns={columns}
                                data={documentScanTypes}
                                isLoading={isLoading}
                                noDataText="No document types found. Create a draft from a template to get started."
                                showPagination={true}
                                defaultPageSize={10}
                            />
                        </Card>
                    </section>
                </Flex>
            </PageContent>
            {createForm && (
                <CreateDocumentTypeModal
                    form={createForm}
                    templates={templates}
                    isCreating={isCreating}
                    onChange={(updates) => setCreateForm((current) => (current ? { ...current, ...updates } : null))}
                    onClose={() => setCreateForm(null)}
                    onSubmit={handleCreateSubmit}
                />
            )}
        </PageContainer>
    );
}

function DocumentTypes() {
    const { documentScanTypeId } = useParams<{ documentScanTypeId?: string }>();

    if (documentScanTypeId) {
        return <DocumentTypeDetail documentScanTypeId={documentScanTypeId} />;
    }

    return <DocumentTypesList />;
}

export default DocumentTypes;
